import PropTypes from 'prop-types';
import { useEffect, useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { paramCase } from 'change-case';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import { Box, Card, Avatar, Typography, CardContent, Stack, Link, Button, IconButton, Tooltip, Paper, Popover } from '@mui/material';
import { tooltipClasses } from '@mui/material/Tooltip';
// routes
import { PATH_HelpAndCommunication } from '../../../routes/paths';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// utils
import { fDate } from '../../../utils/formatTime';
// components
import Image from '../../../components/image';
import Iconify from '../../../components/iconify';
import TextMaxLine from '../../../components/text-max-line';
import Scrollbar from '../../../components/scrollbar';
import SubCategoryTags from "../../admin/subcategory/subcategorytags";
// --------------------------------------------------------------------------------------------------------------

SubCategoryTile.propTypes = {
    index: PropTypes.number,
    post: PropTypes.object,
};
// --------------------------------------------------------------------------------------------------------------

export default function SubCategoryTile({ post, index }) {

    const isDesktop = useResponsive('up', 'md');
    const navigate = useNavigate();

    const { subCategoryServerCoverImage, subCategoryName, subCategoryTags, subCategoryDescription, updatedDate, topicTypeName, ownerFranchise, article } = post.items[0];

    const handleOpenFolder = () => {
        navigate(PATH_HelpAndCommunication.helpandcommunicationsub(post.items[0].categoryName, post.items[0].subCategoryName), { state: { subCategoryTopicData: post.items } });
    }

    return (
        <Card onClick={handleOpenFolder}
            sx={{ display: 'flex', flexDirection: 'column', height: 250, width: 'auto', borderRadius: 1, cursor: 'pointer', }}>
            <Box sx={{ position: 'relative', height: '40%', width: '100%', mb: 1 }}>
                <Image alt="cover"
                    src={`/UploadFiles/HelpTopic/CoverImages/${subCategoryServerCoverImage}`}
                    // ratio="4/3"
                    sx={{ objectFit: 'fill', width: '100%', height: '100%' }}
                />
            </Box>

            <Box sx={{ width: '100%', height: '60%' }}>
                <PostContent
                    title={subCategoryName}
                    topictype={topicTypeName}
                    comment={subCategoryDescription}
                    createdAt={updatedDate}
                    index={index}
                    franchise={ownerFranchise}
                    tags={subCategoryTags}
                    handleOpenFolder={handleOpenFolder}
                    post={post.items[0]}
                />
            </Box>
        </Card>
    );
}

// ---------------------------------------------------------------------------------------------------------------

PostContent.propTypes = {
    topictype: PropTypes.string,
    title: PropTypes.string,
    comment: PropTypes.string,
    createdAt: PropTypes.string,
    tags: PropTypes.string,
    post: PropTypes.object,
    handleOpenFolder: PropTypes.func,
};

export function PostContent({ title, topictype, comment, createdAt, tags, handleOpenFolder, post }) {

    //  const linkTo = PATH_HelpAndCommunication.helpandcommunicationviewpage(paramCase(title));

    const CustomWidthTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))({
        [`& .${tooltipClasses.tooltip}`]: {
            maxWidth: 500,
        },
    });

    const handleDownloadClick = (serverDocName, docName) => {
        const url = `/UploadFiles/HelpTopic/Documents/${serverDocName}`
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            docName
        );

        document.body.appendChild(link);    // Append to html link element page

        link.click();    // Start download

        link.parentNode.removeChild(link);  // Clean up and remove the link
    }

    const DateChecker = (dateToCheck) => {
        const inputDate = new Date(dateToCheck);
        const currentDate = new Date();
        const timeDifference = currentDate - inputDate;
        const daysDifference = timeDifference / (1000 * 60 * 60 * 24);
        return daysDifference <= 15;
    };

    return (
        <CardContent sx={{ p: 1, pb: 0 }} >
            <Stack flexWrap="wrap" direction="row" justifyContent="space-between" alignItems="space-between" spacing={2} sx={{ color: 'text.disabled' }}>
                {/* <Iconify icon="foundation:burst-new" width='25px' height='25px' color="success.main" /> */}

                <Typography variant="caption" component="div" sx={{ color: 'text.disabled', pt: 0.5 }} >
                    {fDate(createdAt)}
                </Typography>

                {DateChecker(createdAt) &&
                    <>
                        <Iconify icon="clarity:new-line" width='25px' height='25px' color="success.main" sx={{ mr: 1 }} />
                    </>
                }
            </Stack>

            <TextMaxLine
                variant='body2'
                persistent
                color="blue"
                line={1}
                sx={{ fontSize: '0.80rem' }}
            >
                {title}
            </TextMaxLine>

            <Stack direction="row" alignItems="flex-start" justifyContent="flex-start">
                <CustomWidthTooltip title={comment}>
                    <Typography variant="caption"
                        sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            WebkitLineClamp: 3,
                            height: '2.7em',  // Set a fixed height for two lines
                            transition: 'all 0.3s ease',
                            fontSize: '0.62rem',
                        }}
                    >
                        {comment}
                    </Typography>
                </CustomWidthTooltip>
            </Stack>

            <Stack flexWrap="wrap" direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 0.5, height: '3em' }}>
                <Scrollbar>
                    <Paper>
                        <SubCategoryTags post={post} />
                    </Paper>
                </Scrollbar>

                {/* <Box sx={{ position: 'absolute', bottom: 2, right: 2 }} >
                    <Tooltip title="View More" variant="solid">
                        <IconButton onClick={handleOpenFolder}>
                            <Iconify icon="material-symbols-light:folder-open" width='18px' height='18px' color="primary.main" />
                        </IconButton>
                    </Tooltip>
                </Box>  */}
            </Stack>
        </CardContent>
    );
}
